import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { FilterChipsRow, FloorChip, SectionDot } from './styles';
import { FloorplanPlacementSection } from '../types';

interface SectionChipsProps {
  sections: FloorplanPlacementSection[];
  /** Null means every section — the "All" chip. */
  selectedSectionUuid: string | null;
  onSelect: (sectionUuid: string | null) => void;
}

interface SectionChipProps {
  section: FloorplanPlacementSection;
  isActive: boolean;
  onSelect: (sectionUuid: string | null) => void;
}

const SectionChip = ({ section, isActive, onSelect }: SectionChipProps): JSX.Element => {
  // Selecting the active section again clears it, so the row always has a way back to All.
  const handleClick = useCallback(() => onSelect(isActive ? null : section.uuid), [onSelect, isActive, section.uuid]);

  return (
    <FloorChip
      type="button"
      data-testid={`floorplan-section-chip-${section.uuid}`}
      isActive={isActive}
      aria-pressed={isActive}
      onClick={handleClick}
    >
      <SectionDot color={section.color} />
      {section.name}
    </FloorChip>
  );
};

/**
 * The sections of the floor on screen, as one chip each behind an "All" chip.
 *
 * The dot carries the section's own colour, so a chip reads as the same section the admin coloured on
 * the plan.
 */
export const SectionChips = ({ sections, selectedSectionUuid, onSelect }: SectionChipsProps): JSX.Element => {
  const intl = useIntl();
  const selectAll = useCallback(() => onSelect(null), [onSelect]);

  return (
    <FilterChipsRow data-testid="floorplan-section-chips">
      <FloorChip type="button" isActive={!selectedSectionUuid} aria-pressed={!selectedSectionUuid} onClick={selectAll}>
        {intl.formatMessage({ id: 'all' })}
      </FloorChip>
      {sections.map(section => (
        <SectionChip
          key={section.uuid}
          section={section}
          isActive={section.uuid === selectedSectionUuid}
          onSelect={onSelect}
        />
      ))}
    </FilterChipsRow>
  );
};
